import { useState, useCallback } from 'react';
import { isPiBrowserEnvironment, getEnvironment, debugLog, errorLog } from '../utils/environment';
import PiAdService from '../services/PiAdService'; // 파일 생성 예정
import { verifyRewardedAd } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../contexts/AppContext';
import { APP_MODE } from '../constants/AppModes';

// AppContext에서 ppPoints 등을 갱신하는 함수가 필요할 수 있음 (Main.js 참고)

/**
 * Pi Ads 사용을 위한 Custom Hook
 * @param {Function} onReward - (선택) 보상 지급 성공 시 콜백 (클라이언트 상태 업데이트용)
 * @returns {Object} { showAd, isLoading, error, lastAdDebugInfo, clearLastAdDebugInfo }
 */
const usePiAds = () => {
    const { t } = useTranslation();
    const { userId: contextUserId, setAppMode } = useAppContext(); // [Fix] Context에서 userId + setAppMode 가져오기
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    /** 모바일 디버깅용: URL에 ?debug=<REACT_APP_LOG_ACCESS_KEY> 있을 때 화면에 마지막 에러 표시 */
    const [lastAdDebugInfo, setLastAdDebugInfo] = useState(null);

    // Pi User ID를 가져오기 위해 sessionStorage 또는 Context 사용
    const getUserId = () => {
        if (contextUserId) return contextUserId;

        // Fallback: sessionStorage
        const storedId = sessionStorage.getItem('pi-user-id');
        if (storedId) return storedId;

        const storedUser = sessionStorage.getItem('pi-user');
        if (storedUser) {
            try {
                return JSON.parse(storedUser).uid;
            } catch (e) { return null; }
        }
        return null;
    };

    /**
     * 보상형 광고 표시 및 검증
     * @param {string} rewardType - 'star' | 'hint' | 'heart'
     * @param {Function} onSuccess - 성공 시 콜백
     * @param {Function} onError - 실패 시 콜백
     */
    const showAd = useCallback(async (rewardType, onSuccess, onError) => {
        setIsLoading(true);
        setError(null);
        const environment = getEnvironment();
        const userId = getUserId();
        let adWasShown = false; // 광고 시청 직후 검증 실패 시 안내 문구 분기용

        try {
            if (isPiBrowserEnvironment()) {
                // 1. Pi Environment
                debugLog('[usePiAds] Pi 환경에서 광고 요청:', rewardType);

                // 1-1. 광고 표시 요청 (Pi SDK)
                // 🎯 [Safety] Pause heavy tasks during ad
                let adId;
                try {
                    setAppMode(APP_MODE.AD_WATCHING);
                    adId = await PiAdService.showRewardedAd(rewardType);
                } finally {
                    setAppMode(APP_MODE.NORMAL);
                }

                adWasShown = true; // 광고 시청 완료 후 검증 단계

                // 1-2. 백엔드 검증 요청
                debugLog('[usePiAds] 광고 시청 완료, 서버 검증 시작. AdId:', adId);

                if (!userId) {
                    throw new Error('USER_ID_NOT_FOUND');
                }

                const verifyResult = await verifyRewardedAd(adId, rewardType, userId, environment);

                if (verifyResult.success && verifyResult.verified) {
                    debugLog('[usePiAds] 광고 검증 성공, 보상 지급:', verifyResult);
                    if (onSuccess) onSuccess(verifyResult);
                } else {
                    throw new Error(verifyResult.message || 'AD_VERIFICATION_FAILED');
                }

            } else {
                // 2. Browser / Dev Environment (Fallback)
                debugLog('[usePiAds] 웹 환경(비-Pi)에서 광고 요청 (개발 모드)', rewardType);

                const isConfirmed = window.confirm(
                    `[개발 모드] ${rewardType} 보상 광고 시뮬레이션\n\n'확인'을 누르면 광고를 다 본 것으로 처리하고 보상을 지급합니다.`
                );

                if (isConfirmed) {
                    // Mock 성공 응답
                    const mockResult = {
                        success: true,
                        verified: true,
                        rewardType,
                        newBalance: rewardType === 'star' ? 9999 : undefined, // Mock balance
                        message: 'Dev Mode Mock Success'
                    };
                    if (onSuccess) onSuccess(mockResult);
                } else {
                    debugLog('[usePiAds] 개발 모드 광고 시청 취소됨');
                    // 취소는 에러가 아님, 그냥 아무 동작 안함 or onError?
                    // 보통 광고 닫기는 에러가 아님.
                }
            }
        } catch (err) {
            errorLog('[usePiAds] 광고 처리 중 오류:', err);
            setError(err);
            // 모바일에서 URL ?debug=KEY 로 접속 시 화면에 표시할 상세 정보
            setLastAdDebugInfo({
                phase: adWasShown ? 'verify' : 'show',
                message: err?.message || '',
                code: err?.code,
                status: err?.status,
                details: err?.details,
                timestamp: Date.now()
            });
            const msg = err?.message || '';
            // ADS_NOT_SUPPORTED: 테스트넷/샌드박스에서는 광고 미지원(플랫폼 제한), 메인넷에서는 구버전 브라우저
            if (msg.includes('ADS_NOT_SUPPORTED')) {
                const env = getEnvironment();
                const isTestEnv = env === 'sandbox' || env === 'testnet';
                const text = isTestEnv
                    ? (t('ad_not_available_testnet') || '테스트 환경에서는 광고를 사용할 수 없습니다. 메인넷에서 이용해 주세요.')
                    : (t('ad_update_browser') || 'Pi Browser를 최신 버전으로 업데이트해 주세요.');
                alert(text);
                if (onError) onError(err);
                return;
            }
            if (onError) onError(err);
            else {
                // 광고 시청까지 완료된 뒤 검증/네트워크 실패인 경우 별도 안내
                const msg = err?.message || '';
                const isVerificationFailure = adWasShown || msg.includes('AD_VERIFICATION_FAILED') || msg.includes('User not found') || msg.includes('Rate limit');
                const alertMessage = isVerificationFailure
                    ? (t('ad_verification_failed') || '보상 검증에 실패했습니다. 잠시 후 다시 시도해 주세요.')
                    : (t('ad_load_failed') || '광고를 불러오는데 실패했습니다.');
                alert(alertMessage);
            }
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    const clearLastAdDebugInfo = useCallback(() => setLastAdDebugInfo(null), []);

    return {
        showAd,
        isLoading,
        error,
        lastAdDebugInfo,
        clearLastAdDebugInfo
    };
};

export default usePiAds;
